import {
    EFFECT_KEYS,
    type EffectKey,
    SchemaWidgetNavigateMessage,
    SchemaWidgetNotifyMessage,
    SchemaWidgetOpenMessage,
} from '@luminovo/apps-types';
import { createSnackbar, useNavigate } from '@luminovo/design-system';
import { useSnackbar } from 'notistack';
import { useMemo, useState } from 'react';
import type { z } from 'zod';

import { widgetNavigationState } from '../routing/widgetNavigationState';
import { hasTransientActivation } from './userActivation';

// What the product does for each effect an app can ask for. The keys and their
// response shapes are declared in `@luminovo/apps-types`; this is the half that
// touches the browser, and a test pins the two sets equal.

type Navigate = ReturnType<typeof useNavigate>;
type EnqueueSnackbar = ReturnType<typeof useSnackbar>['enqueueSnackbar'];

/** The app an effect is carried out for. */
export interface EffectContext {
    appId: string;
}

// `widget.navigate`'s own path rule and bounds, so an app's answer and a framed
// widget can never disagree about where an app may send the user. The path is
// authored by the app: the rule admits only a root-relative product path, so the
// navigation stays on this origin and still faces the destination route's own
// permission check.
const SchemaNavigateEffect = SchemaWidgetNavigateMessage.pick({ path: true, replace: true });
export type NavigateEffect = z.infer<typeof SchemaNavigateEffect>;

interface DownloadEffect {
    url: string;
}

// `widget.open`'s own URL rule and bounds: `https:`, `mailto:` or `tel:`.
const SchemaOpenEffect = SchemaWidgetOpenMessage.pick({ url: true });
export type OpenEffect = z.infer<typeof SchemaOpenEffect>;

// `widget.notify`'s own message bounds and closed set of levels.
const SchemaNotifyEffect = SchemaWidgetNotifyMessage.pick({ message: true, level: true });
export type NotifyEffect = z.infer<typeof SchemaNotifyEffect>;

/** A notification is shown for a frame, under the name of the app that sent it. */
export interface NotifyContext extends EffectContext {
    /** Shown as the snackbar's title, so the user can tell which app is talking. */
    appName: string;
    /** The frame instance that asked. A reloaded frame is a new instance, so it starts afresh. */
    frame: string;
}

// One notification per second from the same frame. The message comes from a
// customer-authored bundle, so a render loop would otherwise bury the page in
// snackbars.
const NOTIFY_MIN_INTERVAL_MS = 1000;

/**
 * The throttle's memory: the frame that notified last, and when. Only the last
 * one is kept, so a notification from a different frame always shows and resets
 * the window.
 */
export interface NotifyThrottle {
    frame: string;
    lastAt: number;
}

function newNotifyThrottle(): NotifyThrottle {
    return { frame: '', lastAt: Number.NEGATIVE_INFINITY };
}

// A handler is split in two so a whole result is checked before anything runs.
// `parse` answers undefined for a payload not in the declared shape, which the
// caller reports as the authoring fault it is.
interface EffectHandler<T> {
    parse: (payload: Record<string, unknown>) => T | undefined;
    run: (value: T, context: EffectContext) => void;
}

type EffectPayloads = { download: DownloadEffect; navigate: NavigateEffect };
type Handlers = { [K in EffectKey]: EffectHandler<EffectPayloads[K]> };

function createHandlers(navigate: Navigate): Handlers {
    return {
        download: {
            // Only `https:` is followed: an app answering `javascript:` would
            // otherwise get script execution on the product origin for free.
            parse: ({ url }) => {
                if (typeof url !== 'string') return undefined;
                try {
                    return new URL(url).protocol === 'https:' ? { url } : undefined;
                } catch {
                    return undefined;
                }
            },
            // The run is two awaits from the click, so a popup would be blocked by
            // then. Navigating costs nothing: the download is served as an
            // attachment, so the browser takes the file and leaves this page where
            // it is.
            run: ({ url }) => window.location.assign(url),
        },
        navigate: {
            parse: (payload) => {
                const parsed = SchemaNavigateEffect.safeParse(payload);
                return parsed.success ? parsed.data : undefined;
            },
            // Stamped on the history entry so the destination can tell an app sent
            // the user there, as for a framed widget's `widget.navigate`.
            run: ({ path, replace }, { appId }) =>
                navigate({ href: path, replace: replace ?? false, state: widgetNavigationState(appId) }),
        },
    };
}

/** Exported so a test can pin it against the declared keys. */
export const HANDLED_EFFECT_KEYS = Object.keys(createHandlers(() => {})) as EffectKey[];

// Each pair leaves the user in two places at once: a download is a navigation of
// its own, and whichever ran second would cancel the first. Neither runs, and
// both are reported as malformed.
const CONFLICTING: ReadonlyArray<readonly [EffectKey, EffectKey]> = [['download', 'navigate']];

export interface AppliedEffects {
    /** Effects the app asked for and the product carried out. */
    applied: EffectKey[];
    /** Effects it asked for in a shape, or a combination, the product could not act on. */
    malformed: EffectKey[];
}

export interface EffectHandlers {
    /**
     * Carry out whatever an app's answer asks for.
     *
     * `result` is the object the effects sit on: what a workflow returned, one level
     * inside the run response's executor envelope, or a worker.js handler's whole
     * answer. Anything outside the declared effect keys is the app's own data and
     * is left alone.
     */
    applyEffects: (result: unknown, context: EffectContext) => AppliedEffects;
    /** Navigate for an app whose destination the host already knows, such as a `navigate` button. */
    navigate: (effect: NavigateEffect, context: EffectContext) => void;
    /**
     * Open a URL in a new tab, for a framed widget's `widget.open`. Answers whether it did.
     *
     * Not an effect a result may carry, and it must stay that way: a result arrives
     * awaits after the click, when the browser blocks a popup anyway. So it is
     * refused unless the user is interacting with the page right now.
     */
    open: (effect: OpenEffect) => boolean;
    /** Show a short message under the app's name, for a framed widget's `widget.notify`. Answers whether it did. */
    notify: (effect: NotifyEffect, context: NotifyContext) => boolean;
}

function asObject(value: unknown): Record<string, unknown> | undefined {
    return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : undefined;
}

function runEffect<K extends EffectKey>(
    handler: EffectHandler<EffectPayloads[K]>,
    payload: Record<string, unknown> | undefined,
    context: EffectContext,
): boolean {
    const value = payload === undefined ? undefined : handler.parse(payload);
    if (value === undefined) return false;
    handler.run(value, context);
    return true;
}

/**
 * The effect handlers, bound to a `navigate` and an `enqueueSnackbar`. Exported so a test can pass spies.
 *
 * `throttle` is passed in so the hook can keep it across a re-created set of handlers.
 */
export function createEffectHandlers(
    navigate: Navigate,
    enqueueSnackbar: EnqueueSnackbar,
    throttle: NotifyThrottle = newNotifyThrottle(),
): EffectHandlers {
    const handlers = createHandlers(navigate);
    return {
        applyEffects: (result, context) => {
            const body = asObject(result);
            if (body === undefined) return { applied: [], malformed: [] };
            const requested = EFFECT_KEYS.filter((key) => key in body);
            const conflicting = CONFLICTING.find(([a, b]) => requested.includes(a) && requested.includes(b));
            if (conflicting !== undefined) return { applied: [], malformed: requested };

            const applied: EffectKey[] = [];
            const malformed: EffectKey[] = [];
            for (const key of requested) {
                const ok = runEffect(
                    handlers[key] as EffectHandler<EffectPayloads[typeof key]>,
                    asObject(body[key]),
                    context,
                );
                (ok ? applied : malformed).push(key);
            }
            return { applied, malformed };
        },
        navigate: (effect, context) => handlers.navigate.run(effect, context),
        open: (effect) => {
            const parsed = SchemaOpenEffect.safeParse(effect);
            if (!parsed.success || !hasTransientActivation()) return false;
            // Always a new tab, product URLs included: `widget.navigate` is the
            // same-tab intent, and a widget that asks to open its link elsewhere
            // wants to keep its own state, such as a loaded assembly, on screen.
            window.open(parsed.data.url, '_blank', 'noopener,noreferrer');
            return true;
        },
        notify: (effect, { appName, frame }) => {
            const parsed = SchemaNotifyEffect.safeParse(effect);
            if (!parsed.success) return false;
            const now = Date.now();
            if (throttle.frame === frame && now - throttle.lastAt < NOTIFY_MIN_INTERVAL_MS) return false;
            throttle.frame = frame;
            throttle.lastAt = now;
            enqueueSnackbar(
                ...createSnackbar({
                    // As text, never markup: the message is the app's.
                    title: appName,
                    message: parsed.data.message,
                    overrides: { notistackSnackbarOptions: { variant: parsed.data.level } },
                }),
            );
            return true;
        },
    };
}

/**
 * What the product does when an app asks it to: from a workflow's result or a
 * handler's answer, from a button whose action is an effect in itself, or from a
 * framed widget's bridge message.
 *
 * A hook because `navigate` needs the router and `notify` the snackbar. Call what
 * it returns only from a click handler and its continuation, or from a message a
 * framed widget sent.
 */
export function useEffectHandlers(): EffectHandlers {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [throttle] = useState(newNotifyThrottle);
    return useMemo(
        () => createEffectHandlers(navigate, enqueueSnackbar, throttle),
        [navigate, enqueueSnackbar, throttle],
    );
}
